// src/section/CMS/components/customPage/ImageTextFeatureRenderer.tsx
//
// "Feature Cards" — a row of equal-width cards, each with an image (with the
// heading overlaid), body text and a button. Cards keep a fixed width (sized
// for a 4-up row) so fewer than four do NOT stretch to fill, every card stays
// on one row, and all buttons align on the same baseline (flex column with the
// button pushed to the bottom of equal-height cards). Below the md breakpoint
// the row stacks into a single full-width column. More than four cards scroll
// horizontally on desktop.
import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTypography } from '../../../../context/TypographyContext';
import { getFileServerBase } from './fileServerBase';

const FILE_SERVER_BASE = `${getFileServerBase()}/media/large/`;

// Desktop column count the fixed card width is sized for.
const COLS = 4;
const GAP_PX = 16;

const resolveMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${FILE_SERVER_BASE}${url}`;
};

interface ImageTextItem {
  id?: string;
  image?: string;
  heading?: string;
  body?: string;
  button_label?: string;
  button_url?: string;
  image_side?: 'left' | 'right'; // legacy field, ignored by the card layout
}

interface ImageTextFeatureRendererProps {
  sectionHeading?: string;
  maxWidth?: string;
  items: ImageTextItem[];
}

const FeatureCard: React.FC<{ item: ImageTextItem }> = ({ item }) => {
  const { styleFor } = useTypography();
  const navigate = useNavigate();

  const image = resolveMediaUrl(item.image);
  const heading = item.heading ?? '';
  const buttonLabel = item.button_label ?? '';
  const buttonUrl = item.button_url ?? '';

  if (!image && !heading && !item.body) return null;

  const paragraphs = (item.body ?? '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const handleClick = () => {
    if (!buttonUrl) return;
    try {
      const url = new URL(buttonUrl);
      if (url.origin === window.location.origin) navigate(url.pathname + url.search);
      else window.open(buttonUrl, '_blank', 'noopener');
    } catch {
      navigate(buttonUrl);
    }
  };

  return (
    <Box
      sx={{
        // Mobile/tablet: full-width stacked card. Desktop: fixed width sized
        // for a 4-up row so cards never stretch to fill.
        flex: '0 0 auto',
        width: {
          xs: '100%',
          md: `calc((100% - ${(COLS - 1) * GAP_PX}px) / ${COLS})`,
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image with heading overlay */}
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 5', overflow: 'hidden', bgcolor: '#eee' }}>
        {image && (
          <Box
            component="img"
            src={image}
            alt={heading}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {heading && (
          <>
            {/* Scrim for legibility */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(0,0,0,0.35) 100%)',
                pointerEvents: 'none',
              }}
            />
            <Typography
              component="h3"
              sx={{
                ...styleFor('tile.overlayLabel', { color: true }),
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '8%',
                textAlign: 'center',
                px: 2,
              }}
            >
              {heading}
            </Typography>
          </>
        )}
      </Box>

      {/* Body */}
      {paragraphs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2.5, mb: { xs: 2.5, md: 3 } }}>
          {paragraphs.map((p, i) => (
            <Typography key={i} sx={{ ...styleFor('section.body') }}>
              {p}
            </Typography>
          ))}
        </Box>
      )}

      {/* Button — mt:auto keeps it at the bottom so buttons align across cards */}
      {buttonLabel && buttonUrl && (
        <Button
          variant="outlined"
          onClick={handleClick}
          disableElevation
          sx={{
            mt: 'auto',
            alignSelf: 'stretch',
            border: '1px solid #000',
            color: '#000',
            borderRadius: 0,
            fontWeight: 600,
            fontSize: '12px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            py: 1.5,
            '&:hover': { bgcolor: '#000', color: '#fff', border: '1px solid #000' },
          }}
        >
          {buttonLabel}
        </Button>
      )}
    </Box>
  );
};

const ImageTextFeatureRenderer: React.FC<ImageTextFeatureRendererProps> = ({ sectionHeading, maxWidth, items }) => {
  const { styleFor } = useTypography();

  if (!Array.isArray(items) || items.length === 0) return null;

  const visible = items.filter((it) => it && (it.image || it.heading || it.body));
  if (visible.length === 0) return null;

  const mw = (maxWidth ?? '').trim();

  return (
    <Container
      disableGutters
      maxWidth={mw ? false : 'lg'}
      sx={{ px: 0, ...(mw ? { maxWidth: mw, mx: 'auto' } : {}) }}
    >
      {sectionHeading && (
        <Typography component="h2" sx={{ ...styleFor('section.heading'), mb: { xs: 3, md: 4 } }}>
          {sectionHeading}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          // Mobile/tablet: stack into a single full-width column.
          // Desktop (md+): one row of fixed-width cards.
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: 'nowrap',
          alignItems: 'stretch', // equal-height cards so button baselines line up (desktop row)
          gap: { xs: '32px', md: `${GAP_PX}px` },
          overflowX: { xs: 'visible', md: 'auto' },
          pb: { xs: 0, md: 1 },
          // Hide scrollbar visually while keeping it scrollable when it overflows
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 3 },
        }}
      >
        {visible.map((item, i) => (
          <FeatureCard key={item.id ?? i} item={item} />
        ))}
      </Box>
    </Container>
  );
};

export default ImageTextFeatureRenderer;
